// English-only string lookup. Kept as a typed `t()` helper + dictionary so
// common copy lives in one file instead of scattered across components —
// easier to audit and rewrite when branding shifts. Not a real i18n layer:
// a translation (DK, NO, DE…) would need a fresh `Locale` union, a DA
// dictionary, and re-enabling the locale toggle below.
//
// Decision 2026-04-19: staying English-only for the foreseeable future.
// Previous DA dictionary + LocaleToggle + browser-language detection were
// removed because we'd rather ship no translation than a half-translated
// UI. Re-add when we have a real demand from a non-English cohort.
import { createContext, useContext, useMemo, type ReactNode } from "react";

type Dictionary = Record<string, string>;

const DICTIONARIES: Record<"en", Dictionary> = {
  en: {
    "auth.signIn": "Sign in",
    "auth.signingIn": "Signing in…",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.forgot": "Forgot your password?",
    "auth.signUp": "Create an account",
    "auth.noAccount": "New to PX7 Training?",
    "auth.createAccount": "Create your account",
    "auth.signupSub": "Starter is €9/month with the first payment 14 days later. Upgrade anytime for coach dialogue and plan adjustments.",
    "auth.yourName": "Your name",
    "auth.creating": "Creating account…",
    "nav.overview": "Overview",
    "nav.athletes": "Athletes",
    "nav.inbox": "Inbox",
    "nav.admin": "Admin",
    "nav.account": "Account",
    "nav.signOut": "Sign out",
    "checklist.getStarted": "Get started",
    "checklist.threeSteps": "Three steps to your first plan.",
    "checklist.ready": "You're ready — build your first plan.",
    "checklist.selfServe": "Self-serve — no coach needed. Each step takes a couple of minutes.",
    "checklist.coachPath": "Your coach builds the plan once these are in.",
    "checklist.onboardingTitle": "Answer 7 onboarding questions",
    "checklist.onboardingDesc": "Tells us how you're starting and whether to show pace or power.",
    "checklist.onboardingCta": "Start onboarding",
    "checklist.assessmentTitle": "Run your first assessment",
    "checklist.assessmentDesc": "Locks your training zones to real data. About 15 minutes.",
    "checklist.assessmentCta": "Run assessment",
    "checklist.planTitle": "Generate your first plan",
    "checklist.planDesc": "Pick a race or a training block and we'll shape the weeks.",
    "checklist.planCta": "Create plan",
    "checklist.logTitle": "Log your first session",
    "checklist.logDesc": "Every logged session sharpens the plan — it learns from you.",
    "generic.retry": "Try again",
    "generic.save": "Save",
    "generic.cancel": "Cancel",
    "generic.delete": "Delete",
    "generic.edit": "Edit",
    "generic.close": "Close",
    "generic.loading": "Loading",
  },
};

export type TranslationKey = keyof typeof DICTIONARIES["en"];

type Ctx = {
  t: (key: TranslationKey, fallback?: string) => string;
};

const I18nCtx = createContext<Ctx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const value = useMemo<Ctx>(
    () => ({
      t: (key, fallback) => DICTIONARIES.en[key] ?? fallback ?? String(key),
    }),
    [],
  );
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
